<script>
    import { bbox } from '@turf/bbox';
    import { distance } from '@turf/distance';
    import { featureCollection, point } from '@turf/helpers';
    import ArrowRight from 'lucide-svelte/icons/arrow-right';
    import LocateFixed from 'lucide-svelte/icons/locate-fixed';
    import MapPin from 'lucide-svelte/icons/map-pin';
    import { onMount } from 'svelte';
    import tippy from 'tippy.js';
    import { numberWithDelimiter } from './table/utils';

    export let data = null;
    export let zoom;
    export let coordinates;
    export let token;
    export let placeholder;
    // starts and ends with slash
    export let propubUrlRoot = '/';

    let jsonData = {};
    let isDarkMode = false;
    let mapStyle = 'light-v11';
    let mapContainer;
    let map;
    let markers = [];
    let schools = [];
    let searchQuery = '';
    let userLocation = null;
    let locatingUser = false;
    let locatingUserFailed = false;
    let locationFailureText = '';
    let userTooFar = false;
    let listContainer;

    $: updateMarkers(filteredSchools);

    // Function to update markers based on filteredSchools
    const updateMarkers = (filteredSchools) => {
        // Remove existing markers
        markers.forEach((marker) => marker.remove());
        markers = [];

        // Add markers for filtered schools
        filteredSchools.forEach((school) => {
            if (school.latitude && school.longitude) {
                const el = document.createElement('div');
                el.classList.add('marker');
                el.classList.add(school.race);

                const marker = new mapboxgl.Marker(el).setLngLat([school.longitude, school.latitude]).addTo(map);

                const tooltipHTML = `
                    <h2 class="tooltip-header">${school.name}</h2>
                    <p class="tooltip-content-small">
                    ${school.address}
                    <br/>
                    ${[school.city, school.state, school.zip].filter((str) => str).join(', ')}
                    </p>
                    <p class="tooltip-content-small">
                        ${[school.grades, school.school_type, school.religion].filter((str) => str).join(' • ')}
                    `;

                tippy(el, {
                    placement: 'bottom',
                    arrow: true,
                    animation: 'fade',
                    allowHTML: true,
                    content: tooltipHTML,
                });

                el.addEventListener('click', () => {
                    window.location = school.url;
                });

                // Store the marker for later removal
                markers.push(marker);
            }
        });
    };

    $: filteredSchools = searchQuery
        ? schools.filter((school) => school.name.toLowerCase().includes(searchQuery.toLowerCase()))
        : schools;

    const formatMiles = (miles) => {
        const feet = miles * 5280;
        if (feet < 5280 * 0.25) {
            return `${numberWithDelimiter(feet.toFixed(0))}&thinsp;ft.`;
        } else {
            return `${numberWithDelimiter(miles)}&thinsp;mi.`;
        }
    };

    const fetchData = async (data) => {
        const { slug } = data;

        try {
            const url = propubUrlRoot + [data.type == 'district' ? 'districts' : 'states', `${slug}.json`].join('/');
            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch schools');

            const parsedData = await response.json();
            return parsedData;
        } catch (error) {
            console.error('Error fetching schools:', error);
        }
    };

    const setMapStyle = () => {
        mapStyle = isDarkMode ? 'dark-v11' : 'light-v11';
        if (map) {
            map.setStyle(`mapbox://styles/mapbox/${mapStyle}`);
        }
    };

    onMount(async () => {
        jsonData = await fetchData(data);
        schools = jsonData.schools;

        // Check for initial color scheme preference
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            isDarkMode = true;
            setMapStyle();
        }

        // Listen for changes in color scheme
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
            isDarkMode = event.matches;
            setMapStyle();
        });

        map = new mapboxgl.Map({
            container: mapContainer,
            style: `mapbox://styles/mapbox/${mapStyle}`,
            accessToken: token,
            center: coordinates,
            zoom: zoom,
            cooperativeGestures: true,
        });

        const geolocate = new mapboxgl.GeolocateControl({
            trackUserLocation: false,
        });

        map.addControl(geolocate);

        geolocate.on('geolocate', (e) => {
            const { longitude } = e.coords;
            const { latitude } = e.coords;

            userLocation = { latitude, longitude };

            findNearestSchools();
        });

        const points = jsonData.schools.map((location) => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [location.longitude, location.latitude],
            },
            properties: {},
        }));

        const featureCollection = {
            type: 'FeatureCollection',
            features: points,
        };

        const featureBounds = bbox(featureCollection);

        const districtBbox = bbox(jsonData.geojson);
        const paddingDegrees = 0.5;
        const expandedBbox = [
            districtBbox[0] - paddingDegrees,
            districtBbox[1] - paddingDegrees,
            districtBbox[2] + paddingDegrees,
            districtBbox[3] + paddingDegrees,
        ];

        map.fitBounds(featureCollection.features.length > 1 ? featureBounds : districtBbox, { padding: 20 });

        map.setMaxBounds([
            [expandedBbox[0], expandedBbox[1]], // SW corner
            [expandedBbox[2], expandedBbox[3]], // NE corner
        ]);

        map.on('load', async () => {
            map.addLayer({
                id: `leaid-${jsonData.leaid || jsonData.id}`,
                type: 'fill',
                source: {
                    type: 'geojson',
                    data: jsonData.geojson,
                },
                layout: {},
                paint: {
                    'fill-color': '#777777',
                    'fill-opacity': 0.1,
                },
            });

            map.addLayer({
                id: `${jsonData.leaid || jsonData.id}-outline`,
                type: 'line',
                source: {
                    type: 'geojson',
                    data: jsonData.geojson,
                },
                layout: {},
                paint: {
                    'line-color': '#ffffff',
                    'line-width': 1,
                },
            });

            updateMarkers(filteredSchools);
        });
    });

    /**
     * Retrieves the user's location.
     */
    const useLocation = () => {
        if (navigator.geolocation) {
            locatingUser = true;
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    userLocation = { latitude, longitude };
                    findNearestSchools();
                },
                (error) => {
                    console.error('Error getting location:', error);
                    locatingUserFailed = true;
                    locationFailureText = 'An error occurred while trying to retrieve your location.';
                },
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
            locatingUserFailed = true;
            locationFailureText = 'Your browser doesn’t support geolocation.';
        }
        locatingUser = false;
    };

    /**
     * Finds the private schools nearest to the user using Turf.js
     */
    const findNearestSchools = () => {
        if (!userLocation) return;

        const userPoint = point([userLocation.longitude, userLocation.latitude]);

        const schoolPoints = schools.map((school) => ({
            point: point([school.longitude, school.latitude]),
            school,
        }));

        // Calculate distances from the user to each school
        schoolPoints.forEach((schoolPoint) => {
            schoolPoint.distance = distance(userPoint, schoolPoint.point, { units: 'miles' });
        });

        // Filter out schools that are too far away
        const MAX_DISTANCE_MILES = 50;
        const nearbySchools = schoolPoints.filter((schoolPoint) => schoolPoint.distance <= MAX_DISTANCE_MILES);

        // If there are no nearby schools, handle this case
        if (nearbySchools.length === 0) {
            handleNoNearbySchools();
            return;
        }

        // Sort schools by distance and take the nearest 10
        filteredSchools = nearbySchools
            .sort((a, b) => a.distance - b.distance)
            .map((schoolPoint) => ({
                ...schoolPoint.school,
                distance: schoolPoint.distance.toFixed(2), // Add distance to the school object
            }));

        listContainer.scrollTop = 0;

        // Calculate bounding box for the nearest schools
        const nearestSchoolPoints = filteredSchools
            .slice(0, 10)
            .map((school) => point([school.longitude, school.latitude]));
        const nearestSchoolsBox = bbox(featureCollection(nearestSchoolPoints));

        // Fit the map to the bounding box
        map.fitBounds(nearestSchoolsBox, {
            padding: 20,
        });
    };

    const handleNoNearbySchools = () => {
        userTooFar = true;
    };
</script>

<div class="container">
    <div class="left-panel">
        <div class="map" bind:this={mapContainer} />
    </div>
    <div class="right-panel">
        <div class="input-container">
            <input type="text" inputmode="search" class="search-input" bind:value={searchQuery} {placeholder} />
            <p class="location-control">
                {#if userTooFar}
                    There are no schools in this {data.type} near you. Search manually or view all private schools in the
                    {data.type} instead.
                {:else if locatingUserFailed}
                    {locationFailureText} Search manually or view all private schools in the {data.type} instead.
                {:else if locatingUser}
                    <button class="location-link" disabled>
                        <span class="spin"><LocateFixed size={12} /></span> Finding Your Location…
                    </button>
                {:else}
                    <button class="location-link" on:click={useLocation}>
                        <LocateFixed size={12} /> Use Your Current Location
                    </button>
                {/if}
            </p>
        </div>
        <div class="list-container" bind:this={listContainer}>
            <ul class="school-list">
                {#each filteredSchools as school}
                    <li class="school-item">
                        <p>
                            <a href={school.url}>{school.name}</a>
                        </p>
                        <p class="school-item-address">
                            <span>
                                <a
                                    href="#!"
                                    role="button"
                                    aria-label={`Zoom to ${school.name}`}
                                    on:keydown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ')
                                            map.flyTo({ center: [school.longitude, school.latitude], zoom: 14 });
                                    }}
                                    class="school-item-address-link"
                                    on:click|preventDefault={() => {
                                        map.flyTo({ center: [school.longitude, school.latitude], zoom: 14 });
                                    }}
                                >
                                    <MapPin />
                                    {[school.address, school.city, school.state].filter((str) => str).join(', ')}
                                </a>
                                {#if school.grades}
                                    •
                                    {school.grades.toUpperCase()}
                                {/if}
                            </span>

                            {#if school.distance}
                                <span>
                                    {@html formatMiles(school.distance)}
                                </span>
                            {/if}
                        </p>
                    </li>
                {/each}
            </ul>
        </div>
        <div class="more-container">
            <a class="more-link" href="#schools">
                See More <ArrowRight size={12} />
            </a>
        </div>
    </div>
</div>

<style lang="scss">
    @keyframes spin {
        from {
            transform: rotate(0deg);
        }

        to {
            transform: rotate(360deg);
        }
    }

    .container {
        display: flex;
        width: 100%;
        height: 500px;
        margin: var(--spacing1) auto;
        background: transparent;
        gap: var(--spacing0);
    }

    .left-panel {
        flex: 2;
    }

    .map {
        width: 100%;
        height: 500px;
    }

    .right-panel {
        display: flex;
        flex: 1;
        flex-direction: column;
        padding: 0 !important;

        div {
            margin: 0 (--spacing0);
        }
    }

    .location-control {
        color: var(--color-text-supp);
        font-size: var(--scale-2);
        line-height: 150%;
    }

    .more-container {
        text-align: right;
    }

    .more-link,
    .location-link {
        padding: 6px 8px;
        border-radius: 4px;
        background-color: #006997 !important;
        color: #fff !important;
        cursor: pointer;
        font-size: var(--scale-2);

        &:hover {
            background-color: #06aee0 !important;
            text-decoration: none !important;
        }

        &:disabled {
            background-color: var(--gray-40) !important;
        }
    }

    .spin {
        display: inline-block;
        animation: spin 5000ms linear infinite;
    }

    .search-input {
        width: 100%;
        padding: var(--spacing-1);
        border: 1px solid var(--gray-20);
        border-radius: 4px !important;
        margin: 0 0 var(--spacing0);
        font-size: 16px;

        &:focus {
            background-color: #fff;
        }
    }

    .list-container {
        flex: 1;
        border-bottom: 1px solid #ddd;
        margin: var(--spacing-1) 0;
        overflow-y: auto;
    }

    .school-list {
        padding: 0;
        margin: 0 !important;
        list-style: none;
    }

    .school-item {
        padding: var(--spacing-2) 0;
        border-bottom: 1px solid #ddd;

        p {
            margin: var(--spacing-2) 0;
            line-height: 125%;
        }

        &-address {
            display: flex;
            justify-content: space-between;
            color: var(--color-text-meta);
            font-size: var(--scale-2);

            &-link {
                color: inherit !important;

                &:hover {
                    cursor: pointer;
                    text-decoration: underline;
                }
            }
        }
    }

    @media only screen and (width <= 48em) {
        .container {
            height: auto;
            flex-direction: column;
        }

        .left-panel,
        .right-panel {
            width: 100%;
            height: auto;
            flex: none;
        }

        #map {
            height: 100%;
        }

        .list-container {
            max-height: calc(50vh);
        }
    }
</style>
